import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search, Mail } from "lucide-react";
import { toast } from "sonner";
import FileAttachmentField, { AttachmentBadge } from "@/components/FileAttachmentField";
import ExportButton from "@/components/ExportButton";

const STATUS = ["Baru","Diproses","Selesai","Diarsipkan"];
const KATEGORI = ["Regulasi","Operasional","Training","Perlengkapan","SDM","Lainnya"];
const DIVISI = ["Pilot","FA"];

const stBadge = (s) => {
  const m = { Baru:"bg-sky-100 text-sky-700", Diproses:"bg-amber-100 text-amber-700", Selesai:"bg-emerald-100 text-emerald-700", Diarsipkan:"bg-slate-200 text-slate-700" };
  return <Badge className={`${m[s] || m.Baru} hover:${m[s]}`}>{s}</Badge>;
};

/**
 * Unified Surat page — merges Pilot + FA existing collections (preserves data).
 * Existing routes /surat/pilot-* remain working; this page mirrors the same data.
 */
export default function SuratUnifiedPage({ arah, title }) {
  // arah = "masuk" | "keluar"
  const qc = useQueryClient();
  const { canWrite, hasModule } = useAuth();
  const canPilot = hasModule("surat_pilot");
  const canFA = hasModule("surat_fa");
  const canWritePilot = canWrite("surat_pilot");
  const canWriteFA = canWrite("surat_fa");
  const anyWrite = canWritePilot || canWriteFA;

  const [tab, setTab] = useState("ALL"); // ALL | Pilot | FA
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const pathPilot = `surat/pilot-${arah}`;
  const pathFA = `surat/fa-${arah}`;

  const { data: pilotData = [] } = useQuery({
    queryKey: [pathPilot],
    queryFn: async () => (await apiClient.get(`/${pathPilot}`)).data,
    enabled: canPilot,
  });
  const { data: faData = [] } = useQuery({
    queryKey: [pathFA],
    queryFn: async () => (await apiClient.get(`/${pathFA}`)).data,
    enabled: canFA,
  });

  const merged = useMemo(() => [
    ...(canPilot ? pilotData.map(x=>({...x, __divisi:"Pilot"})) : []),
    ...(canFA ? faData.map(x=>({...x, __divisi:"FA"})) : []),
  ].sort((a,b)=> (b.updated_at||b.created_at||"").localeCompare(a.updated_at||a.created_at||"")), [pilotData, faData, canPilot, canFA]);

  const filtered = useMemo(() => merged.filter(r => {
    const matchTab = tab === "ALL" || r.__divisi === tab;
    const matchS = status === "ALL" || r.status === status;
    const term = q.toLowerCase();
    const matchQ = !q || [r.nomor_surat, r.perihal, r.pengirim, r.tujuan, r.pic].some(v => (v||"").toLowerCase().includes(term));
    return matchTab && matchS && matchQ;
  }), [merged, tab, status, q]);

  const pathFor = (divisi) => divisi === "Pilot" ? pathPilot : pathFA;
  const canWriteFor = (divisi) => divisi === "Pilot" ? canWritePilot : canWriteFA;

  const mSave = useMutation({
    mutationFn: async (b) => {
      const p = pathFor(b.__divisi);
      const clean = {...b}; delete clean.__divisi;
      return b.id ? (await apiClient.put(`/${p}/${b.id}`, clean)).data : (await apiClient.post(`/${p}`, clean)).data;
    },
    onSuccess: () => { qc.invalidateQueries({queryKey:[pathPilot]}); qc.invalidateQueries({queryKey:[pathFA]}); toast.success("Surat disimpan"); setOpen(false); },
    onError: (e) => toast.error(e.response?.data?.detail || "Gagal menyimpan"),
  });
  const mDel = useMutation({
    mutationFn: async (r) => (await apiClient.delete(`/${pathFor(r.__divisi)}/${r.id}`)).data,
    onSuccess: () => { qc.invalidateQueries({queryKey:[pathPilot]}); qc.invalidateQueries({queryKey:[pathFA]}); toast.success("Surat dihapus"); },
  });

  const openNew = () => {
    const defaultDivisi = canWritePilot ? "Pilot" : "FA";
    const base = arah === "masuk"
      ? { nomor_surat:"", tanggal_surat:"", tanggal_diterima:"", pengirim:"", perihal:"", kategori: KATEGORI[0], pic:"", status:"Baru", catatan:"", attachment: null }
      : { nomor_surat:"", tanggal_surat:"", tujuan:"", perihal:"", kategori: KATEGORI[0], pic:"", status:"Baru", catatan:"", attachment: null };
    setEditing({ ...base, __divisi: defaultDivisi });
    setOpen(true);
  };
  const openEdit = (r) => { setEditing({...r}); setOpen(true); };

  const exportCols = arah === "masuk"
    ? [{key:"__divisi",label:"Divisi"},{key:"nomor_surat",label:"Nomor"},{key:"tanggal_surat",label:"Tgl Surat"},{key:"tanggal_diterima",label:"Diterima"},{key:"pengirim",label:"Pengirim"},{key:"perihal",label:"Perihal"},{key:"kategori",label:"Kategori"},{key:"pic",label:"PIC"},{key:"status",label:"Status"}]
    : [{key:"__divisi",label:"Divisi"},{key:"nomor_surat",label:"Nomor"},{key:"tanggal_surat",label:"Tgl Surat"},{key:"tujuan",label:"Tujuan"},{key:"perihal",label:"Perihal"},{key:"kategori",label:"Kategori"},{key:"pic",label:"PIC"},{key:"status",label:"Status"}];

  return (
    <div className="space-y-6" data-testid={`surat-${arah}-page`}>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-[#00A651]">JKTOF · Surat</div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>{title}</h1>
        <p className="text-sm text-slate-500 mt-1">Manajemen surat divisi Pilot &amp; FA (data terpisah, gabungan tampilan).</p>
      </div>

      <Card className="border-slate-200"><CardContent className="p-4 space-y-4">
        <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="ALL" data-testid={`surat-${arah}-tab-all`}>Semua</TabsTrigger>
              {canPilot && <TabsTrigger value="Pilot" data-testid={`surat-${arah}-tab-pilot`}>Pilot</TabsTrigger>}
              {canFA && <TabsTrigger value="FA" data-testid={`surat-${arah}-tab-fa`}>FA</TabsTrigger>}
            </TabsList>
          </Tabs>
          <div className="flex flex-col sm:flex-row gap-2 flex-1 lg:justify-end">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Cari nomor/perihal..." className="pl-9" value={q} onChange={(e)=>setQ(e.target.value)} data-testid={`surat-${arah}-search`}/>
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Status"/></SelectTrigger>
              <SelectContent><SelectItem value="ALL">Semua Status</SelectItem>{STATUS.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            {anyWrite && <Button onClick={openNew} className="bg-[#00A651] hover:bg-[#008741]" data-testid={`surat-${arah}-add`}><Plus className="w-4 h-4 mr-1"/> Tambah</Button>}
            <ExportButton rows={filtered} columns={exportCols} title={title} filename={`surat-${arah}`} testid={`surat-${arah}-export`}/>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 overflow-x-auto">
          <Table className="fodm-table">
            <TableHeader className="bg-slate-50"><TableRow>
              <TableHead>Divisi</TableHead>
              <TableHead>Nomor</TableHead>
              <TableHead>Tgl Surat</TableHead>
              {arah === "masuk" ? <TableHead>Diterima</TableHead> : null}
              <TableHead>{arah === "masuk" ? "Pengirim" : "Tujuan"}</TableHead>
              <TableHead>Perihal</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead>PIC</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Lampiran</TableHead>
              {anyWrite && <TableHead className="text-right">Aksi</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={anyWrite ? (arah==="masuk"?11:10) : (arah==="masuk"?10:9)} className="text-center py-8 italic text-slate-500">Tidak ada surat.</TableCell></TableRow>
              ) : filtered.map(r => (
                <TableRow key={`${r.__divisi}-${r.id}`} data-testid={`surat-${arah}-row`}>
                  <TableCell><Badge variant="outline">{r.__divisi}</Badge></TableCell>
                  <TableCell className="font-mono text-xs">{r.nomor_surat}</TableCell>
                  <TableCell className="text-xs">{r.tanggal_surat}</TableCell>
                  {arah === "masuk" && <TableCell className="text-xs">{r.tanggal_diterima}</TableCell>}
                  <TableCell className="text-sm">{arah === "masuk" ? r.pengirim : r.tujuan}</TableCell>
                  <TableCell className="max-w-xs truncate text-sm">{r.perihal}</TableCell>
                  <TableCell><Badge variant="outline">{r.kategori}</Badge></TableCell>
                  <TableCell className="text-xs">{r.pic}</TableCell>
                  <TableCell>{stBadge(r.status)}</TableCell>
                  <TableCell><AttachmentBadge path={r.attachment?.path} name={r.attachment?.name} testid={`surat-${arah}-att`}/></TableCell>
                  {anyWrite && (
                    <TableCell className="text-right">
                      {canWriteFor(r.__divisi) && (
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={()=>openEdit(r)} data-testid={`surat-${arah}-edit`}><Pencil className="w-4 h-4"/></Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-red-600" data-testid={`surat-${arah}-delete`}><Trash2 className="w-4 h-4"/></Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Hapus surat?</AlertDialogTitle><AlertDialogDescription>Surat <b>{r.nomor_surat}</b> ({r.__divisi}) akan dihapus.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={()=>mDel.mutate(r)}>Hapus</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Ubah" : "Tambah"} Surat {arah === "masuk" ? "Masuk" : "Keluar"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Divisi</Label>
                <Select value={editing.__divisi} onValueChange={(v)=>setEditing({...editing, __divisi:v})} disabled={!!editing.id}>
                  <SelectTrigger data-testid={`surat-${arah}-form-divisi`}><SelectValue/></SelectTrigger>
                  <SelectContent>{DIVISI.filter(d => canWriteFor(d)).map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Nomor Surat</Label><Input value={editing.nomor_surat} onChange={(e)=>setEditing({...editing, nomor_surat:e.target.value})}/></div>
              <div><Label>Tanggal Surat</Label><Input type="date" value={editing.tanggal_surat||""} onChange={(e)=>setEditing({...editing, tanggal_surat:e.target.value})}/></div>
              {arah === "masuk"
                ? <div><Label>Tanggal Diterima</Label><Input type="date" value={editing.tanggal_diterima||""} onChange={(e)=>setEditing({...editing, tanggal_diterima:e.target.value})}/></div>
                : <div><Label>Tujuan</Label><Input value={editing.tujuan||""} onChange={(e)=>setEditing({...editing, tujuan:e.target.value})}/></div>}
              {arah === "masuk" && <div className="col-span-2"><Label>Pengirim</Label><Input value={editing.pengirim||""} onChange={(e)=>setEditing({...editing, pengirim:e.target.value})}/></div>}
              <div className="col-span-2"><Label>Perihal</Label><Input value={editing.perihal||""} onChange={(e)=>setEditing({...editing, perihal:e.target.value})}/></div>
              <div><Label>Kategori</Label>
                <Select value={editing.kategori} onValueChange={(v)=>setEditing({...editing, kategori:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{KATEGORI.map(k=><SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>PIC</Label><Input value={editing.pic||""} onChange={(e)=>setEditing({...editing, pic:e.target.value})}/></div>
              <div><Label>Status</Label>
                <Select value={editing.status} onValueChange={(v)=>setEditing({...editing, status:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{STATUS.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Lampiran</Label>
                <FileAttachmentField value={editing.attachment && typeof editing.attachment === "object" ? editing.attachment : null} onChange={(v)=>setEditing({...editing, attachment: v})} testid={`surat-${arah}-upload`}/>
              </div>
              <div className="col-span-2"><Label>Catatan</Label><Input value={editing.catatan||""} onChange={(e)=>setEditing({...editing, catatan:e.target.value})}/></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button className="bg-[#00A651] hover:bg-[#008741]" onClick={()=>mSave.mutate(editing)} data-testid={`surat-${arah}-save`}>Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function SuratMasukPage() { return <SuratUnifiedPage arah="masuk" title="Surat Masuk"/>; }
export function SuratKeluarPage() { return <SuratUnifiedPage arah="keluar" title="Surat Keluar"/>; }
